<script setup lang="ts">
import { Department, TranslatedProduct } from "../../../types/PersologOnlineTypes";
import { useI18n } from "vue-i18n";
import { onMounted, watch, ref, PropType } from "vue";

const { t } = useI18n();

defineProps({
    product: {
        type: Object as PropType<TranslatedProduct>,
        required: true,
    },
});

const emit = defineEmits(["valid", "update:departments"]);

/**
 * List of departments, controlled by the chips component
 */
const departments = ref<Department[]>([]);
const newDepartmentInput = ref('');

function removeDepartment(department: string) {
    departments.value = departments.value.filter((d) => d.name !== department);
    emit("update:departments", departments.value);
}

/**
 * Adds a department from the input field
 */
const addDepartment = () => {
    const input = newDepartmentInput.value.trim();
    if (input.length === 0) return;

    // Split on commas and semicolons, allow periods
    const departmentNames = input.split(/[,;]+/)
        .map(name => name.trim())
        .filter(name => name.length > 0);

    // Add each department if it doesn't already exist
    departmentNames.forEach(name => {
        if (departments.value.some(d => d.name.toLowerCase() === name.toLowerCase())) {
            return;
        }

        departments.value.push({ name });
    });

    newDepartmentInput.value = '';
    emit("update:departments", departments.value);
};

/**
 * Handle keydown events on the input
 */
const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ',') {
        event.preventDefault();
        addDepartment();
    }
};

/**
 * Validates this configuration tab.
 * If the product has departments, it checks if there are any departments selected.
 * Emits the "valid" event with the result.
 */
const validate = () => {
    emit("valid", true);
    return true;
};
defineExpose({ validate });

watch(departments, () => {
    validate();
});

onMounted(() => {
    validate();
});
</script>

<template>
    <FloatLabel class="card p-fluid w-full mb-8" data-cy="configure-departments-tab">
        <div class="chips-input-wrapper" id="departmentChips" data-cy="departments-input-wrapper">
            <Chip
                v-for="department in departments"
                :key="department.name"
                removable
                :label="department.name"
                class="department-chip p-chips-token"
                :data-cy="`department-chip-${department.name.toLowerCase().replace(/\s+/g, '-')}`"
                @remove="removeDepartment(department.name)"
            />
            <InputText
                v-model="newDepartmentInput"
                class="department-input"
                :placeholder="departments.length === 0 ? t('project-configuration.tabs.departmentsPlaceholder') : 'Add department...'"
                @keydown="handleKeyDown"
                @blur="addDepartment"
                size="small"
                data-cy="new-department-input"
            />
        </div>
        <label class="p-inputwrapper-filled" for="departmentChips" data-cy="departments-label">
            {{ t("project-configuration.projectDepartments") }}
        </label>
    </FloatLabel>
    <p data-cy="departments-description">
        {{ t("project-configuration.tabs.departmentsDescription") }}
    </p>
</template>

<style scoped>
.chips-input-wrapper {
    border: 1px solid var(--p-surface-border);
    border-radius: 6px;
    padding: 0.5rem;
    min-height: 2.5rem;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.25rem;
}

.chips-input-wrapper:focus-within {
    border-color: var(--primary-color);
}

.department-chip {
    background-color: var(--p-secondary-500);
    color: white;
    border-radius: 16px;
}

.department-chip :deep(.p-chip-remove-icon) {
    color: white;
}

.department-input {
    background: transparent;
    flex: 1;
    min-width: 120px;
    margin-left: 0.25rem;
}
</style>
